<template>
<div class='scheduler-container'>
  <!--div>
    <select v-model='columnFilter'>
      <option v-for='f in columnGroups' :value='f'>{{f.name}}</option>
    </select>
  </div-->
  <div class="schedule-header">
    <div v-for='c in columns'>{{c.name}}</div>
  </div>
  <div class='scheduler-scroll-container'>
  <div class="schedule-container">
    <div class="schedule-timeline">
      <div v-for='t in displayTicks' :key='t[1]'>{{t[1]}}</div>
    </div>
    <div v-for='(c,i) in columns' class='schedule-column' :data-columnid='i'>
      <div 
        v-show='shadowPeriodColumn === c'
        class="shadow-period"
        :class="{'shadow-period-hidden': !startedAdd}" v-bind:style="{top:shadowPeriodStart+'px', height: shadowPeriodEnd+'px'}"
      >
        <div class='shadow-period-coords'>
          start:{{shadowPeriodStartTimeFormatted}}
          end:{{shadowPeriodEndTimeFormatted}}
        </div>
      </div>
      <div class="period"
        v-for='period in c.periods'
        :key='period.id'
        :style="{top:periodToPixels(period).start+'px', height: periodToPixels(period).height+'px', width: c.offsets[period.id][0]+'%', 'margin-left': c.offsets[period.id][1]+'px'}"
        :class="{'period-shadow': c.offsets[period.id][2], 'period-editing': editDlg.pinnedTo === period}"
        @click.self='editPeriod(period)'
      >
        <div class="period-header">
          <div>{{formatPeriodTitle(period)}}</div>
          <div class="period-time">{{formatTime(period.start)}} - {{formatTime(period.end)}}</div>
          <div class="period-time">{{period.location}}</div>
        </div>
      </div>
    </div>
  </div>
  </div>
</div>
</template>

<script>
import interact from "interactjs";
import { DateTime, Interval } from "luxon";
const columnGroupFunctions = {
  start: {
    name: "start",
    display: period => {
      return period.start.toLocaleString(DateTime.DATE_MED);
    },
    filter: period => {
      return period.start.day;
    },
    defaults: periods => {
      return newPeriod => {
        newPeriod.start = newPeriod.start.set({ day: periods[0].start.day });
        newPeriod.end = newPeriod.end.set({ day: periods[0].start.day });
        return newPeriod;
      };
    }
  },
  location: {
    name: "location",
    display: period => {
      return "Location: " + period.room;
    },
    filter: period => {
      return period.room;
    },
    defaults: periods => {
      return newPeriod => {
        newPeriod.room = periods[0].room;
        return newPeriod;
      };
    }
  }
};
export { columnGroupFunctions };

export default {
  props: {
    maxHeight: {
      type: Number,
      default: function() {
        return 500;
      }
    },
    existingPeriods: {
      type: Array,
      default: function() {
        return [];
      }
    },
    columnGroups: {
      type: Object,
      default: function() {
        return [columnGroupFunctions];
      }
    },
    defaultOptions: {
      type: Object,
      default: function() {
        return {};
      }
    },
    columnFilter: {
      type: Object
    },
    start: {
      default: function() {
        return DateTime.local().startOf("day");
      }
    },
    editingPeriod: {
      default: function() {
        return null;
      }
    }
  },
  data() {
    return {
      startedAdd: false,
      shadowPeriodStart: null,
      shadowPeriodEnd: null,
      shadowPeriodColumn: null,
      stepSizeSeconds: 3600 / 2,
      height: null,
      editDlg: {
        pinnedTo: null
      },
      unsavedPeriods: []
    };
  },
  watch: {
    editingPeriod: function(newV, oldV) {
      if (!newV && oldV) {
        this.closeEdit(this.editDlg.pinnedTo);
      }
    }
  },
  computed: {
    periods: function() {
      return this.existingPeriods.concat(this.unsavedPeriods);
    },
    columns: function() {
      /*
      [
        {
          periods: [],
          name: "string",
          defaults: function{period}() => period,
          overlaps: [],
          offsets: "Npx"
        },
        ...
      ]
      */
      var periodGroups = this.periods.reduce((result, p) => {
        const key = this.columnFilter.filter(p);
        result[key] = result[key] || {
          periods: [],
          id: Object.keys(result).length
        };
        result[key].periods.push(p);
        return result;
      }, {});
      var columns = [];
      for (var key in periodGroups) {
        columns.push({
          periods: periodGroups[key].periods,
          name: this.columnFilter.display(periodGroups[key].periods[0]),
          defaults: this.columnFilter.defaults(periodGroups[key].periods)
        });
      }
      columns.forEach(c => {
        c.overlaps = this.calcPeriodOverlaps(c.periods);
        c.offsets = this.calcPeriodOffsets(c.overlaps);
      });

      if (this.columnFilter && this.defaultOptions[this.columnFilter.name]) {
        this.defaultOptions[this.columnFilter.name].forEach(option => {
          const defaultOption = {};
          defaultOption[this.columnFilter.name] = option;
          if (
            Object.keys(periodGroups).indexOf(
              String(this.columnFilter.filter(defaultOption))
            ) === -1
          ) {
            // key is not in existing columns, so let's add an empty column
            const defaultOption = {};
            defaultOption[this.columnFilter.name] = option;

            columns.push({
              periods: [],
              name: this.columnFilter.display(defaultOption),
              defaults: this.columnFilter.defaults([defaultOption])
            });
          }
        });
      }
      columns.sort((a, b) => {
        if (a.name < b.name) return -1;
        if (a.name > b.name) return 1;
        return 0;
      });

      return columns;
    },
    shadowPeriodStartTime: function() {
      var start = 86400 * this.shadowPeriodStart / this.height;
      return this.start.plus({ seconds: start });
    },
    shadowPeriodEndTime: function() {
      var duration = 86400 * this.shadowPeriodEnd / this.height;
      return this.shadowPeriodStartTime.plus({ seconds: duration });
    },
    shadowPeriodStartTimeFormatted: function() {
      return this.shadowPeriodStartTime.toLocaleString(DateTime.TIME_SIMPLE);
    },
    shadowPeriodEndTimeFormatted: function() {
      return this.shadowPeriodEndTime.toLocaleString(DateTime.TIME_SIMPLE);
    },
    displayTicks: function() {
      var n = this.start;
      var result = [];
      const startDay = n.day;

      while (n.day === startDay) {
        result.push([n, n.toLocaleString(DateTime.TIME_SIMPLE)]);
        n = n.plus({ hours: 1 });
      }
      return result;
    },
    ticks: function() {
      if (this.editDlg.pinnedTo) {
        var start = this.editDlg.pinnedTo.start;
      }
      const startDay = start.day;
      var result = [];
      while (start.day === startDay) {
        result.push([start, start.toLocaleString(DateTime.TIME_SIMPLE)]);
        start = start.plus({ seconds: this.stepSizeSeconds });
      }
      return result;
    }
  },
  methods: {
    calcPeriodOffsets: function(overlaps) {
      var result = {};
      for (var periodId in overlaps) {
        if (overlaps[periodId].length == 0) {
          width = 90;
        } else {
          var width = 150 / (overlaps[periodId].length + 1);
        }

        var offset = overlaps[periodId].filter(v => v < periodId).length * 32;
        var shadow = offset > 0;
        result[periodId] = [width, offset, shadow];
      }
      return result;
    },
    calcPeriodOverlaps: function(periods) {
      var result = {};
      periods.forEach(p1 => {
        const p1Interval = Interval.fromDateTimes(p1.start, p1.end);
        result[p1.id] = [];

        periods.forEach(p2 => {
          if (p2 == p1) return;
          const p2Interval = Interval.fromDateTimes(p2.start, p2.end);
          if (p2Interval.overlaps(p1Interval)) {
            result[p1.id].push(p2.id);
          }
        });
      });
      return result;
    },
    stepsToPixels: function() {
      return this.height * this.stepSizeSeconds / 86400;
    },
    periodToPixels: function(p) {
      var startSeconds = p.start.diff(this.start).as("seconds") % 86400;
      var start = this.height * startSeconds / 86400;
      var durationSeconds = p.end.diff(p.start).as("seconds");
      var duration = this.height * durationSeconds / 86400;
      return {
        start: start,
        height: duration
      };
    },
    formatTime: function(datetime) {
      return datetime.toLocaleString(DateTime.TIME_SIMPLE);
    },
    formatPeriodTitle: function(period) {
      if (!period.title) return "No Title";

      var suffix = "";
      const truncLength = 40;
      if (period.title.length > truncLength) suffix = "...";
      return period.title.substring(0, truncLength) + suffix;
    },
    addPeriod: function(start, end) {
      var result = {
        start: start,
        end: end,
        id: this.periods.length,
        title: null,
        location: null,
        unsaved: true
      };
      this.unsavedPeriods.push(result);
      return result;
    },
    editPeriod: function(period) {
      setTimeout(() => {
        this.editDlg.pinnedTo = period;
        this.$emit("update:editingPeriod", period);
      }, 0);
    },
    closeEdit: function(period) {
      if (period.unsaved) {
        const i = this.unsavedPeriods.indexOf(period);
        this.unsavedPeriods.splice(i, 1);
      }
      setTimeout(() => {
        this.editDlg.pinnedTo = null;
      }, 0);
    },
    removePeriod: function(period) {
      this.$emit("periodRemoved", period);
    }
  },
  mounted: function() {
    this.scrollOffset = document.getElementsByClassName(
      "scheduler-scroll-container"
    )[0].scrollTop;
    this.height = document.getElementsByClassName(
      "schedule-container"
    )[0].offsetHeight;
    interact(".schedule-column")
      .pointerEvents({})
      .on("down", event => {
        if (!event.target.classList.contains("schedule-column")) return;
        const columnIndex = event.target.getAttribute("data-columnid");
        this.shadowPeriodColumn = this.columns[columnIndex];
        this.startedAdd = true;
        console.log(event);

        const scrollOffset = document.getElementsByClassName(
          "scheduler-scroll-container"
        )[0].scrollTop;
        var y =
          event.clientY -
          event.target.offsetTop +
          window.pageYOffset +
          scrollOffset;
        this.height = event.target.offsetHeight;
        this.shadowPeriodStart =
          Math.floor(y / this.stepsToPixels()) * this.stepsToPixels();
      })
      .on("move", event => {
        var target = event.target;
        while (!target.classList.contains("schedule-column")) {
          target = target.parentNode;
        }
        if (this.startedAdd) {
          const scrollOffset = document.getElementsByClassName(
            "scheduler-scroll-container"
          )[0].scrollTop;
          var y =
            event.clientY -
            target.offsetTop +
            window.pageYOffset +
            scrollOffset;
          requestAnimationFrame(() => {
            this.shadowPeriodEnd =
              Math.floor((y - this.shadowPeriodStart) / this.stepsToPixels()) *
              this.stepsToPixels();
          });
        }
      })
      .on("up", () => {
        if (!this.startedAdd) return;
        this.startedAdd = false;
        var start = 86400 * this.shadowPeriodStart / this.height;
        var duration = 86400 * this.shadowPeriodEnd / this.height;

        const startOffset = {
          days: Math.floor(start / 86400),
          seconds: start - 86400 * Math.floor(start / 86400)
        };
        console.log("offset", startOffset);
        console.log("duration", duration);
        var s = this.start.plus(startOffset);
        var e = s.plus({ seconds: duration });
        var period = this.addPeriod(s, e);
        period = this.shadowPeriodColumn.defaults(period);
        this.editPeriod(period);

        this.shadowPeriodStart = 0;
        this.shadowPeriodEnd = 0;
      });
  }
};
</script>

<style scoped lang='scss'>
.scheduler-container {
  height: 100%;
  display: flex;
  flex-direction: column;
}
.scheduler-scroll-container {
  overflow: auto;
  flex: 1 0 0;
}
.schedule-header {
  margin-left: 50px;
  color: $primary;
  display: flex;
}
.schedule-header > div {
  text-align: center;
  background: $gray-1;
  border-right: 1px solid $gray-4;
  flex-grow: 1;
}
.schedule-container {
  height: 800px;
  display: flex;
  flex-direction: row;
}
.schedule-timeline {
  height: 100%;
  font-size: 0.6rem;
  color: black;
  width: 50px;
  background: white;
  border-right: 1px solid black;
  display: flex;
  flex-direction: column;
  justify-content: space-between;
  align-items: center;
  text-align: center;
}
.schedule-timeline > div {
  width: 100%;
  flex: 1;
  border-top: 1px solid black;
}
.schedule-column {
  flex-grow: 1;
  height: 100%;
  background: white;
  color: black;
  position: relative;
  border-right: 1px solid $gray-4;
  touch-action: none;
}
.shadow-period-hidden {
  display: none !important;
}
.shadow-period {
  z-index: 10;
  border-top: 2px dashed grey;
  border-bottom: 2px dashed grey;
  width: 100%;
  position: absolute;
  display: flex;
  flex-direction: column;
}
.period {
  background: $gray-1;
  border-top: 1px solid $gray-4;
  border-bottom: 1px solid $gray-4;
  border-right: 1px solid $gray-4;
  border-top-right-radius: 4px;
  border-bottom-right-radius: 4px;
  width: 90%;
  position: absolute;
  display: flex;
  flex-direction: column;
  padding: 8px 8px;
}
.period:hover {
  cursor: pointer;
}
.period-shadow {
  border-left: 1px solid $gray-4;
  border-radius: 4px;
  box-shadow: 0 1px 5px rgba(0, 0, 0, 0.15);
}
.period-editing {
  border-left: 1px solid $gray-4;
  border-radius: 4px;
  box-shadow: 0 1px 5px rgba(0, 0, 0, 0.15);
  z-index: 50;
}
.period-header {
  border-left: 3px solid $primary;
  padding-left: 8px;
}
.period-time {
  font-size: 0.8rem;
}

.period-edit-dlg {
  z-index: 100;
  cursor: initial;
  will-change: transform, opacity;
  position: absolute;
  left: 95%;
  width: 400px;
  background: white;
  box-shadow: 0 1px 5px rgba(0, 0, 0, 0.15);
  border-radius: 2px;
  display: flex;
  flex-direction: column;
}
.period-edit-dlg-header {
  display: flex;
  flex-direction: row;
  justify-content: space-between;
  align-items: center;
  border-bottom: 1px solid rgba(0, 0, 0, 0.2);
  padding: 12px;

  span {
    font-size: 1.3rem;
    margin-left: 16px;
  }
}
.period-edit-dlg-body {
  padding: 0px 12px;
}
.period-edit-dlg-body-section {
  padding: 12px 0px;
  display: flex;
  > * {
    flex: 1;
    margin: 0px 12px;
  }
  input {
    width: 100%;
  }

  * {
    display: block;
  }
}
.period-edit-close-btn:hover {
  cursor: pointer;
}
.period-edit-save-btn {
  margin-top: 12px;
  width: 100%;
  border: none;
  background: $green;
  align-self: flex-end;
  color: $gray-1;
  border-bottom-left-radius: 2px;
  border-bottom-right-radius: 2px;
  font-family: "league Spartan";
  font-size: 1.1rem;
  padding: 8px;
}
.shadow-period-coords {
}
</style>



// WEBPACK FOOTER //
// src/components/event/scheduler.vue